// useAdminMetrics.ts — Polls dominus_admin.admin_get_aggregated_metrics().
//
// Auto-refresh every 30 seconds. Returns the last known value while a
// refresh is in flight so the UI never flickers to an empty state.
// Polling stops when the component unmounts.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Identity } from '@dfinity/agent';
import { createAdminAgent, createAdminActor, type AggregatedMetrics } from '../lib/adminAgent';

const POLL_INTERVAL_MS = 30_000;

export interface UseAdminMetricsReturn {
  metrics: AggregatedMetrics | null;
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: Date | null;
  refresh: () => void;
}

export function useAdminMetrics(identity: Identity | null): UseAdminMetricsReturn {
  const [metrics, setMetrics] = useState<AggregatedMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  const fetchMetrics = useCallback(async () => {
    if (!identity) return;

    setIsLoading(true);
    setError(null);

    try {
      const agent = await createAdminAgent(identity);
      const adminActor = createAdminActor(agent);
      const result = await adminActor.admin_get_aggregated_metrics();

      if (!isMountedRef.current) return;

      if ('ok' in result) {
        setMetrics(result.ok);
        setLastFetchedAt(new Date());
      } else {
        setError(result.err);
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to fetch metrics.';
      setError(message);
      console.error('[useAdminMetrics] Fetch error:', err);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [identity]);

  const refresh = useCallback(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Initial fetch + 30s polling
  useEffect(() => {
    if (!identity) return;

    isMountedRef.current = true;
    fetchMetrics();

    timerRef.current = setInterval(fetchMetrics, POLL_INTERVAL_MS);

    return () => {
      isMountedRef.current = false;
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [identity, fetchMetrics]);

  return { metrics, isLoading, error, lastFetchedAt, refresh };
}
